import {
  ActivityIndicator, Pressable, StyleSheet, Text, TouchableOpacity, View,
} from 'react-native';
import * as Animatable from 'react-native-animatable';
import React, {
  Dispatch, SetStateAction, useEffect, useRef, useState,
} from 'react';
import Moment from 'moment';
import { Gesture, GestureDetector } from 'react-native-gesture-handler';
import Animated, {
  runOnJS,
  SharedValue,
  useAnimatedReaction,
  useAnimatedStyle,
  useSharedValue,
} from 'react-native-reanimated';
import { useNavigation } from '@react-navigation/native';
import { useDispatch, useSelector } from 'react-redux';
import { BottomSheetBackdrop, BottomSheetModal, BottomSheetView } from '@gorhom/bottom-sheet';
import Colors from '../../constants/Colors';
import Style from '../../constants/Style';
import MoveIcon from '../16pxIcons/MoveIcon';
import updateAsset from '../../requests/Assets/UpdateAsset';
import isMobile from '../IsMobile';
import TickIcon from '../TickIcon';
import DeleteIcon12 from '../12pxIcons/DeleteIcon12';
import { SAVE_POSITION_SUCCESS, SOMETHING_WENT_WRONG } from '../../constants/AlertMessages';
import { AppScreen } from '../../navigation/LinkingConfiguration';
import { getLocationById } from '../../constants/Utils';
import { Area, Space } from './SetFloorsModal/FloorItem';
import TemplateIcon24 from '../24pxIcons/TemplateIcon24';
import { SelectedTicketState, updateSelectedTicket } from '../../redux/selectedTicketSlice';
import { Ticket } from '../../constants/Types';
import EllipsisIconVertical from '../EllipsisIconVertical';

interface Props {
  asset?: any;
  movingAssetId?: string;
  setMovingItem?: Dispatch<SetStateAction<boolean>>;
  showAlert?: any;
  isPointInShape: any;
  editMode: boolean;
  scale: number;
  negativeScale?: any;
  xStart: number,
  xEnd: number,
  yStart: number,
  yEnd: number,
  mapViewWidth?: number,
  mapViewHeight?: number,
  focusedAssetId?: SharedValue<string | null>;
  setAssetMenuOpen?: Dispatch<SetStateAction<string | null>>;
  assetMenuOpen?: string | null;
  editAssetPosition?: () => void;
  deleteAssetPosition?: () => void;
}

const Asset = ({
  asset, editMode, scale, negativeScale, movingAssetId, isPointInShape,
  setMovingItem, showAlert, mapViewWidth, mapViewHeight, focusedAssetId,
  assetMenuOpen, setAssetMenuOpen, editAssetPosition, deleteAssetPosition,
}: Props) => {
  const buildings: object = useSelector((state:
    { locations: { buildings: object } }) => state.locations.buildings);
  const assetLocation = getLocationById(asset.locationId, buildings);
  const assetLocationPoints = (assetLocation as Area | Space)?.Points?.map((point) => {
    const transformedPoint = { ...point };
    Object.keys(point).forEach((pointKey: string) => {
      transformedPoint[pointKey.toLowerCase()] = point[pointKey];
    });
    return transformedPoint;
  });

  const selectedTicketDetails: SelectedTicketState = useSelector((state:
    { selectedTicket: SelectedTicketState }) => state.selectedTicket);
  const selectedTicket: Ticket | undefined = selectedTicketDetails.ticket;
  const navigation = useNavigation();

  const movingItem = movingAssetId === asset.id;

  const [loading, setLoading] = useState<boolean>(false);
  const [optionsShown, setOptionsShown] = useState<boolean>(false);
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const translateXsen = useSharedValue<number>(asset.GeoX);
  const translateYsen = useSharedValue<number>(asset.GeoY);
  const bottomSheetModalRef = useRef<BottomSheetModal>(null);

  const [isFocused, setIsFocused] = useState<boolean>(false);

  const left = useSharedValue(0);
  const top = useSharedValue(0);

  const dispatch = useDispatch();

  useEffect(() => {
    if (modalVisible) {
      bottomSheetModalRef.current?.present();
    } else {
      bottomSheetModalRef.current?.dismiss();
    }
  }, [modalVisible]);

  useAnimatedReaction(
    () => focusedAssetId?.value === asset.id,
    (focusedValue, previousFocusedValue) => {
      if (focusedValue !== previousFocusedValue && assetMenuOpen !== asset.id) {
        runOnJS(setIsFocused)(focusedValue);
      }
    },
  );

  function assetMoveEnd() {
    if (!assetLocationPoints
      || isPointInShape(translateXsen.value, translateYsen.value, assetLocationPoints)) {
      setOptionsShown(true);
      setMovingItem(true);
    } else setOptionsShown(false);
  }

  const panGestureSen = Gesture.Pan()
    .onChange((event) => {
      translateXsen.value += event.changeX / scale;
      translateYsen.value += event.changeY / scale;
    })
    .onEnd(() => {
      if (isMobile) runOnJS(assetMoveEnd)();
      else assetMoveEnd();
    });

  const save: () => void = () => {
    setLoading(true);
    const updatedCustomFields: any[] = asset.customFields?.map((field) => ({ ...field }))
      ?.filter((field) => !(
        field.name?.toLowerCase() === 'geox' || field.name?.toLowerCase() === 'geoy'
      )) ?? [];
    updatedCustomFields.push({ name: 'GeoX', value: `${translateXsen.value}` });
    updatedCustomFields.push({ name: 'GeoY', value: `${translateYsen.value}` });
    updateAsset({
      assetId: asset.id,
      name: asset.name?.trim(),
      description: asset.description?.trim(),
      manufacturer: asset.manufacturer?.trim(),
      model: asset.model?.trim(),
      serial: asset.serial?.trim(),
      locationId: asset.locationId?.trim(),
      customFields: updatedCustomFields,
      labels: asset.labels,
    }).then((response) => {
      const success = response && !response.isError;
      if (!success) showAlert(SOMETHING_WENT_WRONG, true);
      else {
        (navigation as any)?.setParams?.({ placeAsset: false } as any);
        setOptionsShown(false);
        setMovingItem(false);
        showAlert(SAVE_POSITION_SUCCESS, false);
        asset.GeoX = translateXsen.value;
        asset.GeoY = translateYsen.value;
        asset.customFields = updatedCustomFields;
        dispatch(updateSelectedTicket({ ...selectedTicket, onMap: true, pinned: true }));
      }
      setLoading(false);
    });
  };

  const cancel: () => void = () => {
    translateXsen.value = 0;
    translateYsen.value = 0;
    if ((asset.GeoX === 20 && asset.GeoY === 120) || asset.initialPlacement) {
      asset.hasXY = false;
      asset.GeoX = 0;
      asset.GeoY = 0;
    }
    setOptionsShown(false);
    setMovingItem(false);
  };

  const transformStyleSen = useAnimatedStyle(() => ({
    // @ts-ignore
    transform: [{ translateX: translateXsen.value - 16 }, { translateY: translateYsen.value - 16 }],
  }));

  const positionStyle = useAnimatedStyle(() => {
    let calculatedLeft = 6;
    let calculatedTop = -30;
    const baseOffset = 30;
    const bridgePadding = 8;

    if ((left.value - 300) > mapViewWidth / 2) {
      calculatedLeft = -310;
    }
    if ((top.value - 100) > mapViewHeight / 2) {
      calculatedTop = -184;
    }

    const dialogOnLeft = calculatedLeft === -310;
    const paddingLeft = dialogOnLeft ? 0 : bridgePadding;
    const paddingRight = dialogOnLeft ? bridgePadding : 0;
    const paddingBottom = 0;

    return {
      left: calculatedLeft + baseOffset - paddingLeft,
      top: calculatedTop + baseOffset,
      position: 'absolute',
      opacity: 1,
      height: undefined,
      overflow: 'visible',
      zIndex: isFocused ? 10 : 0,
      paddingLeft,
      paddingRight,
      paddingBottom,
    };
  }, [mapViewWidth, mapViewHeight, asset.id]);

  const assetInfo = () => (
    <Animated.View
      testID='map-asset-details-modal'
      style={isMobile ? { width: '100%' } : positionStyle}
    >
      <View style={[Style.boxShadow, styles.assetInfo, isMobile ? {
        width: '100%',
        marginHorizontal: 0,
      } : { margin: 0 }]}>
        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
          <Text style={styles.buttonText}>{asset.name}</Text>
        </View>
        <Text style={styles.assetInfoTitle}>
          {'Asset info'}
        </Text>
        <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 8 }}>
          <Text style={styles.lastModifiedTitle}>
            {'Last modified:'}
          </Text>
          <Text style={styles.lastModifiedValue}>
            {Moment(asset.updatedOn).format('h:mm a, D MMM')}
          </Text>
        </View>
        <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 16 }}>
          <Text style={styles.areaTitle}>
            {'Area:'}
          </Text>
          <Text style={styles.areaValue}>
            {asset.locationArea ?? '-'}
          </Text>
        </View>
        <TouchableOpacity
          style={{
            borderWidth: 1,
            borderColor: Colors.light.hereworksBlue500,
            borderRadius: 6,
            alignItems: 'center',
          }}
          onPress={() => {
            setModalVisible(false);
            navigation.navigate(AppScreen.TEMPLATE_MANAGEMENT as any, {
              assetId: asset.id ?? '-',
              templateId: asset.templateId,
            });
          }}
          testID='map-navigate-to-asset-btn'
        >
          <Text style={styles.goToAssetTitle}>
            {'Go to asset'}
          </Text>
        </TouchableOpacity>
        {editMode && (
          <TouchableOpacity
            style={{
              position: 'absolute',
              top: 8,
              right: 8,
              padding: 4,
            }}
            onPress={() => {
              setAssetMenuOpen(assetMenuOpen === asset.id ? null : asset.id);
            }}
            testID='map-asset-options-menu-btn'>
            <EllipsisIconVertical />
          </TouchableOpacity>)}
        {(assetMenuOpen === asset.id && editMode) && (
          <View
            style={{
              position: 'absolute',
              top: 40,
              right: -60,
              backgroundColor: '#fff',
              borderRadius: 8,
              borderColor: Colors.light.hereworksBlue200,
              borderWidth: 1,
              zIndex: 4,
            }}
          >
            <TouchableOpacity
              onPress={() => {
                setAssetMenuOpen(null);
                editAssetPosition();
              }}
              testID='map-edit-area-modal-btn'
            >
              <Text style={styles.menuText}>
                Edit Position
              </Text>
            </TouchableOpacity>
            <TouchableOpacity
              onPress={() => {
                setAssetMenuOpen(null);
                deleteAssetPosition();
              }}
            >
              <Text style={styles.menuText}>
                Delete Position
              </Text>
            </TouchableOpacity>
            {loading && (
              <View
                style={{
                  position: 'absolute',
                  justifyContent: 'center',
                  alignItems: 'center',
                  width: '100%',
                  height: '100%',
                  backgroundColor: '#00000040',
                }}
              >
                <ActivityIndicator animating={true} />
              </View>
            )}
          </View>
        )}
      </View>
    </Animated.View>
  );

  const assetIcon = () => (
    <Animatable.View
      style={[
        {
          transform: [
            { scale: isMobile ? 1 : negativeScale },
          ],
        },
      ]}
    >
      <Animated.View>
        <TouchableOpacity
          style={[
            {
              backgroundColor: isFocused ? '#fff' : Colors.light.hereworksBlue500,
              width: movingItem ? 44 : 36,
              height: movingItem ? 44 : 36,
              borderRadius: movingItem ? 22 : 18,
              borderWidth: 2,
              transform: [
              ],
              borderColor: isFocused ? Colors.light.hereworksBlue500 : '#fff',
              alignItems: 'center',
              justifyContent: 'center',
            },
            Style.circleShadow,
          ]}
          onPress={() => {
            if (!editMode && !isMobile) {
              setModalVisible(false);
              navigation.navigate(AppScreen.TEMPLATE_MANAGEMENT as any, {
                assetId: asset.id ?? '-',
                templateId: asset.templateId,
              });
            } else {
              setModalVisible(true);
            }
          }}
        >
          <TemplateIcon24
            fill={isFocused ? '#fff' : Colors.light.hereworksBlue500}
            stroke={isFocused ? Colors.light.hereworksBlue500 : '#fff'} />
        </TouchableOpacity>
      </Animated.View>
      {(!movingItem && !isMobile && (isFocused || assetMenuOpen === asset.id)) && assetInfo()}
    </Animatable.View>
  );

  const Content = () => (isMobile ? <Animated.View style={transformStyleSen}>
    <Pressable
      style={{}}
      onPress={() => {
        if (isMobile) {
          setModalVisible(true);
        }
      }}
    >
      <Animatable.View
        style={{
          backgroundColor: 'transparent',
          flexDirection: 'row',
        }}
      >
        {assetIcon()}
        {movingItem && (
          <>
            <GestureDetector gesture={panGestureSen}>
              <Pressable
                style={(state: { hovered: boolean, pressed: boolean }) => [
                  {
                    opacity: movingItem ? 1 : 0,
                    zIndex: state.hovered ? 10 : 1,
                  },
                  styles.icon,
                ]}
                disabled={!movingItem}
              >
                <MoveIcon />
                {loading && (
                  <View style={styles.loadingIndicator}>
                    <ActivityIndicator animating />
                  </View>
                )}
              </Pressable>
            </GestureDetector>
            {optionsShown && !loading && <>
              <Pressable
                style={(state: { hovered: boolean, pressed: boolean }) => [
                  {
                    opacity: movingItem ? 1 : 0,
                    zIndex: state.hovered ? 10 : 1,
                  },
                  styles.icon, styles.tickIcon,
                ]}
                disabled={!movingItem}
                onPress={save}
              >
                <TickIcon />
              </Pressable>
              <Pressable
                style={(state: { hovered: boolean, pressed: boolean }) => [
                  {
                    opacity: movingItem ? 1 : 0,
                    zIndex: state.hovered ? 10 : 1,
                  },
                  styles.icon, styles.deleteIcon,
                ]}
                disabled={!movingItem}
                onPress={cancel}
              >
                <DeleteIcon12 color={Colors.light.hereworksRed500} />
              </Pressable>
            </>}
          </>
        )}
      </Animatable.View>
    </Pressable>
    <BottomSheetModal
      ref={bottomSheetModalRef}
      backdropComponent={(props) => (
        <BottomSheetBackdrop
          {...props}
          disappearsOnIndex={-1}
          appearsOnIndex={0}
        />
      )}
      // snapPoints={['40%']}
      style={styles.modal}
      onDismiss={() => setModalVisible(false)}
    >
      <BottomSheetView style={{ flex: 1 }}>
        {assetInfo()}
      </BottomSheetView>
    </BottomSheetModal>
  </Animated.View> : <Animated.View style={transformStyleSen}>
    <Animatable.View
      style={{ position: 'absolute' }}
    >
      {movingItem && assetIcon()}
      {!movingItem && (
        <View style={{ position: 'absolute' }}>
          {assetIcon()}
        </View>
      )}
      {movingItem && (
        <View style={{ transform: [{ scale: negativeScale }] }}>
          <GestureDetector gesture={panGestureSen}>
            <Pressable
              style={(state: { hovered: boolean, pressed: boolean }) => [
                {
                  opacity: movingItem ? 1 : 0,
                  zIndex: state.hovered ? 10 : 1,
                },
                styles.icon,
              ]}
              disabled={!movingItem}
            >
              <MoveIcon />
              {loading && (
                <View style={styles.loadingIndicator}>
                  <ActivityIndicator animating />
                </View>
              )}
            </Pressable>
          </GestureDetector>
          {optionsShown && !loading && <>
            <Pressable
              style={(state: { hovered: boolean, pressed: boolean }) => [
                {
                  opacity: movingItem ? 1 : 0,
                  zIndex: state.hovered ? 10 : 1,
                },
                styles.icon, styles.tickIcon,
              ]}
              disabled={!movingItem}
              onPress={save}
            >
              <TickIcon />
            </Pressable>
            <Pressable
              style={(state: { hovered: boolean, pressed: boolean }) => [
                {
                  opacity: movingItem ? 1 : 0,
                  zIndex: state.hovered ? 10 : 1,
                },
                styles.icon, styles.deleteIcon,
              ]}
              disabled={!movingItem}
              onPress={cancel}
            >
              <DeleteIcon12 color={Colors.light.hereworksRed500} />
            </Pressable>
          </>}
        </View>
      )}
    </Animatable.View>
  </Animated.View>);

  return <View
    style={[
      {
        position: 'absolute',
        // zIndex: state.hovered ? 10 : 1,
        top: 0,
        left: 0,
      },
    ]}
    testID='map-asset-icon-on-the-map-btn'
    // disabled={!!movingAssetId}
    key={`${asset.id}-pressable`}
  >
    <Animatable.View
      style={[]}
      onPointerEnter={(event) => {
        if (!isFocused) {
          left.value = event.nativeEvent.x;
          top.value = event.nativeEvent.y;
        }
      }}
    >
      <Content />
    </Animatable.View>
  </View>;
};

const styles = StyleSheet.create({
  buttonText: {
    fontFamily: 'Poppins_400Regular',
    fontSize: 16,
    color: Colors.light.hereworksBlack600,
  },
  modal: {
    margin: 0,
    flex: 1,
  },
  assetInfo: {
    backgroundColor: '#fff',
    width: 280,
    marginHorizontal: 8,
    padding: 12,
    borderRadius: 10,
    cursor: 'auto',
    borderWidth: isMobile ? 0 : 1,
    borderColor: Colors.light.hereworksBlue100,
  },
  assetInfoTitle: {
    fontFamily: 'Poppins_500Medium',
    fontSize: 14,
    lineHeight: 20,
    marginTop: 8,
    marginBottom: 4,
    color: Colors.light.hereworksBlack500,
  },
  lastModifiedTitle: {
    fontFamily: 'Poppins_400Regular',
    fontSize: 12,
    color: Colors.light.hereworksBlack400,
  },
  lastModifiedValue: {
    fontFamily: 'Poppins_500Medium',
    fontSize: 12,
    color: Colors.light.hereworksBlack500,
    flex: 1,
    textAlign: 'right',
  },
  areaTitle: {
    fontFamily: 'Poppins_400Regular',
    fontSize: 12,
    color: Colors.light.hereworksBlack400,
  },
  areaValue: {
    fontFamily: 'Poppins_500Medium',
    fontSize: 12,
    color: Colors.light.hereworksBlack500,
    flex: 1,
    textAlign: 'right',
  },
  goToAssetTitle: {
    fontFamily: 'Poppins_500Medium',
    paddingVertical: 8,
  },
  loadingIndicator: {
    position: 'absolute',
    top: 0,
    left: 0,
    bottom: 0,
    right: 0,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#ffffff50',
  },
  icon: {
    position: 'absolute',
    bottom: -15,
    end: -15,
    alignItems: 'center',
    justifyContent: 'center',
    height: 24,
    width: 24,
    borderColor: Colors.light.hereworksBlue100,
    borderWidth: 1,
    borderRadius: 8,
    backgroundColor: Colors.light.hereworksWhite100,
  },
  tickIcon: {
    end: -40,
  },
  deleteIcon: {
    end: -65,
    backgroundColor: Colors.light.hereworksRed50,
    borderColor: Colors.light.hereworksRed300,
  },
  menuText: {
    fontFamily: 'Poppins_400Regular',
    paddingHorizontal: 12,
    paddingVertical: 9,
    color: Colors.light.hereworksBlack600,
    lineHeight: 21,
  },
});

export default Asset;
